/**
 * Authenticated fetch wrapper.
 *
 * Why this exists:
 *   - JWT is delivered as an HttpOnly cookie, so the JS layer can NOT
 *     read it. That's intentional — XSS cannot exfiltrate it.
 *   - To defend against CSRF we use the double-submit cookie pattern:
 *     the server sets a non-HttpOnly `dwms_csrf` cookie at login. The
 *     client reads it from document.cookie and echoes the value in the
 *     `X-CSRF-Token` header on every state-changing request.
 *   - We always send `credentials: 'include'` so the browser attaches
 *     both cookies.
 *
 * Notes for callers:
 *   - Idle timeout enforcement lives in `useIdleTimeout.js` (UI side)
 *     and `middleware/tokenVerify.js` (server side). When the server
 *     returns 401, this wrapper dispatches a global event so the app
 *     can route to the login screen consistently.
 */

const BASE = process.env.REACT_APP_SERVER_API || '';
const CSRF_COOKIE = 'dwms_csrf';
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

function readCsrfCookie() {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${CSRF_COOKIE}=([^;]+)`));
    return match ? decodeURIComponent(match[1]) : null;
}

function readCurrentBranchId() {
    if (typeof window === 'undefined') return null;
    const raw = localStorage.getItem('currentBranch');
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw);
        return parsed?.id || null;
    } catch {
        return null;
    }
}

function buildHeaders(method, extra = {}) {
    const headers = { ...extra };
    if (!SAFE_METHODS.has(method)) {
        const csrf = readCsrfCookie();
        if (csrf) headers['X-CSRF-Token'] = csrf;
    }
    const branchId = readCurrentBranchId();
    if (branchId) headers['X-Current-Branch'] = branchId;
    return headers;
}

function urlWithQuery(path, params) {
    if (!params) return `${BASE}${path}`;
    const search = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
        if (v === undefined || v === null || v === '') continue;
        search.set(k, v);
    }
    const qs = search.toString();
    return `${BASE}${path}${qs ? `?${qs}` : ''}`;
}

function dispatchAuthExpired() {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent('dwms:auth-expired'));
}

// Translate a raw server error into something safe to show in a toast.
// Keeps known-friendly messages (duplicate-email, validation hints) but
// strips developer-facing strings like "CSRF validation failed" that
// shouldn't reach an end user.
function friendlyMessage(status, body) {
    const raw = body?.message;

    if (status === 401) return 'Your session expired. Please sign in again.';
    if (status === 403) {
        // CSRF failure usually means the session cookie / csrf cookie
        // pair went stale (e.g. logged out in another tab). Surface as
        // a re-auth prompt, not the raw "CSRF validation failed".
        if (/csrf/i.test(raw || '')) return 'Your session expired. Please sign in again.';
        return "You don't have permission for that action.";
    }
    if (status === 404) return 'That record could not be found.';
    if (status === 409) {
        // 409s are usually duplicates — server message is the useful
        // signal ("A patient with this email already exists.") so keep it
        // when it doesn't look like a technical detail.
        if (raw && raw.length < 160 && !/[{}<>]/.test(raw)) return raw;
        return 'That record already exists.';
    }
    if (status === 413) return 'That file is too large to upload.';
    if (status === 429) return 'Too many requests. Please slow down and try again.';
    if (status >= 500) return 'Something went wrong on our end. Please try again.';

    // Other 4xx — use the server message if it looks user-facing,
    // otherwise a generic prompt.
    if (raw && raw.length < 160 && !/[{}<>]/.test(raw)) return raw;
    return "We couldn't complete that. Please check your input and try again.";
}

async function parseJsonOrThrow(res) {
    let body = null;
    try { body = await res.json(); } catch { /* may be empty */ }
    if (res.status === 401 || (res.status === 403 && /csrf/i.test(body?.message || ''))) {
        dispatchAuthExpired();
    }
    if (!res.ok) {
        const err = new Error(friendlyMessage(res.status, body));
        err.status = res.status;
        err.body = body;
        throw err;
    }
    // Returns the parsed server response body directly, e.g.
    // { success, data, message }. Callers access `.data` on it.
    return body;
}

async function request(method, path, { body, params, isFormData = false } = {}) {
    // Auto-detect FormData bodies: they must go out as multipart/form-data
    // with the browser-generated boundary. JSON.stringify(formData) silently
    // serializes to "{}", so the server would see an empty body and reject
    // the request with "Fill all required fields".
    const isForm = isFormData || (typeof FormData !== 'undefined' && body instanceof FormData);
    const headers = buildHeaders(method, isForm ? {} : { 'Content-Type': 'application/json' });
    const url = method === 'GET' ? urlWithQuery(path, params) : `${BASE}${path}`;
    const init = {
        method,
        headers,
        credentials: 'include',
    };
    if (body !== undefined) {
        init.body = isForm ? body : JSON.stringify(body);
    }
    const res = await fetch(url, init);
    return parseJsonOrThrow(res);
}

export const apiGet = (path, params) => request('GET', path, { params });
export const apiPost = (path, body) => request('POST', path, { body: body || {} });
export const apiPut = (path, body) => request('PUT', path, { body: body || {} });
export const apiPatch = (path, body) => request('PATCH', path, { body: body || {} });
export const apiDelete = (path) => request('DELETE', path);
export const apiUpload = (path, formData) => request('POST', path, { body: formData, isFormData: true });
export const apiBuildUrl = (path) => `${BASE}${path}`;
